/**
 * Page Unlock Status Utility
 *
 * Determines which Insights and Govern pages are unlocked based on
 * assessment completion status and available data.
 */

/**
 * Check if maturity assessment has been completed
 */
function hasCompletedMaturity(assessment) {
  if (!assessment) return false
  if (assessment.status === 'completed') return true
  // Check if maturity_assessment responses exist with substantial data
  const ma = assessment.responses?.maturityAssessment
  if (!ma) return false
  return Object.keys(ma).length >= 10 // At least 10 questions answered
}

/**
 * Check if threat context data has been captured
 */
function hasThreatContext(assessment) {
  if (!assessment?.responses) return false
  const tc = assessment.responses.threatContext || assessment.responses.notes?.threatContext
  return !!(tc?.profileKey)
}

/**
 * Check if impact calibration data has been captured
 */
function hasImpactCalibration(assessment) {
  if (!assessment?.responses) return false
  const id = assessment.responses.impactData || assessment.responses.notes?.impactData
  return !!(id?.estimated_annual_revenue || id?.has_cyber_insurance !== undefined)
}

/**
 * Check if the full assessment is complete
 */
function isAssessmentComplete(assessment) {
  return assessment?.status === 'completed'
}

/**
 * Get the unlock status for all gated pages
 * @param {Object} assessment - The latest assessment object with responses
 * @param {Object} client - The client object
 * @returns {Object} Map of pageId → { unlocked, reason, prerequisite }
 */
export function getPageUnlockStatus(assessment, client) {
  const maturityDone = hasCompletedMaturity(assessment)
  const threatDone = hasThreatContext(assessment)
  const impactDone = hasImpactCalibration(assessment)
  const assessmentDone = isAssessmentComplete(assessment)

  return {
    // INSIGHTS
    'program-overview': {
      unlocked: maturityDone,
      reason: 'Complete the maturity assessment to view program insights',
      prerequisite: 'maturity-assessment'
    },
    'domain-analysis': {
      unlocked: maturityDone,
      reason: 'Complete the maturity assessment to view domain analysis',
      prerequisite: 'maturity-assessment'
    },
    'risk-threats': {
      unlocked: maturityDone,
      reason: 'Complete the maturity assessment to view risk analysis',
      prerequisite: 'maturity-assessment'
    },
    'regulatory-landscape': {
      unlocked: true,
      reason: null,
      prerequisite: null
    },
    'compliance-insights': {
      unlocked: maturityDone,
      reason: 'Complete the maturity assessment to view compliance insights',
      prerequisite: 'maturity-assessment'
    },
    'financial-resilience': {
      unlocked: maturityDone,
      reason: 'Complete the maturity assessment to view financial resilience analysis',
      prerequisite: 'maturity-assessment'
    },
    'response-guidance': {
      unlocked: true,
      reason: null,
      prerequisite: null
    },
    // GOVERN
    'enablement-plan': {
      unlocked: assessmentDone,
      reason: 'Complete the Discovery Assessment to generate the enablement plan',
      prerequisite: 'assessment-complete'
    },
    'executive-report': {
      unlocked: false,
      reason: 'Coming soon',
      prerequisite: null
    },
    'risk-register': {
      unlocked: true,
      reason: null,
      prerequisite: null
    }
  }
}

/**
 * Get the navigation path for a prerequisite
 */
export function getPrerequisitePath(prerequisite, clientId) {
  if (!clientId) return '/'
  const base = `/clients/${clientId}`
  switch (prerequisite) {
    case 'maturity-assessment':
      return `${base}?section=assessment`
    case 'threat-context':
      return `${base}?section=risk-threat-context`
    case 'impact-calibration':
      return `${base}?section=risk-impact-calibration`
    case 'assessment-complete':
      return `${base}?section=assessment`
    default:
      return base
  }
}

/**
 * Get engagement lifecycle phase status
 */
export function getLifecycleStatus(assessment, client) {
  const hasResponses = assessment?.responses && Object.keys(assessment.responses).length > 0
  const maturityDone = hasCompletedMaturity(assessment)
  const assessmentDone = isAssessmentComplete(assessment)

  return {
    discover: hasResponses,
    insights: maturityDone,
    govern: assessmentDone,
    deliver: false, // Phase 5 — future
    retainer: client?.status === 'retainer'
  }
}
